import type { Room } from '@studio/models/room';

const prefix = 'iris.initialBilling.pending.';
const pendingRooms = new Set<Room['id']>();

/** A known unfinished setup survives reloads. Missing data cannot clear it.
 * Per absent-data-gates-permissively, only a primary classification resolves this state. */
export const rememberInitialBilling = (roomId: Room['id'], pending: boolean | undefined): void => {
  if (pending === undefined) return;
  if (pending) pendingRooms.add(roomId);
  else pendingRooms.delete(roomId);
  try {
    if (pending) sessionStorage.setItem(prefix + roomId, 'true');
    else sessionStorage.removeItem(prefix + roomId);
  } catch {
    // Storage may be unavailable; the current renderer still retains known pending state.
  }
};

export const initialBillingIsPending = (roomId: Room['id']): boolean => {
  if (pendingRooms.has(roomId)) return true;
  try {
    return sessionStorage.getItem(prefix + roomId) === 'true';
  } catch {
    return false;
  }
};

/** Drop every receipt when the signed-in account ends.
 * Receipts are keyed by room only, and sessionStorage outlives sign-out in the same tab,
 * so the next account could otherwise inherit another account's unfinished setup.
 * Per absent-data-gates-permissively (ai.knowledge/global), a receipt is only evidence
 * about the account that wrote it. */
export const clearInitialBillingReceipts = (): void => {
  pendingRooms.clear();
  try {
    const keys: string[] = [];
    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index);
      if (key !== null && key.startsWith(prefix)) keys.push(key);
    }
    for (const key of keys) sessionStorage.removeItem(key);
  } catch {
    // Storage may be unavailable; the in-memory receipts are already cleared.
  }
};
